import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppPagination from '../../../components/AppPagination';
import AppActionMenu from '../../../components/AppActionMenu';
import ToolsButton from '../../../components/buttons/ToolsButton';
import { useEmployeeDocumentMasterListScreen } from './hooks/useEmployeeDocumentMasterListScreen';

export default function EmployeeDocumentMasterListScreen() {
  const { state, actions } = useEmployeeDocumentMasterListScreen();
  const navigate = useNavigate();

  const statusTabs = [
    { key: 'active', label: 'Active' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' },
  ];

  const toolsOptions = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export to Excel', icon: 'file_download' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Masters</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">Employee Document Master</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Employee Document Master
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Design and manage standardized HR document and certificate templates with dynamic merge tokens.
          </p>
        </div>
      </section>

      {/* Filter & Actions Row */}
      <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Search, Category Filter & Reset */}
        <div className="flex flex-wrap items-center gap-3 w-full max-w-2xl">
          <label className="relative flex-1 min-w-[220px]">
            <span className="material-symbols-outlined pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-outline text-lg">
              search
            </span>
            <input
              type="text"
              value={state.searchText}
              onChange={(event) => actions.setSearchText(event.target.value)}
              placeholder="Search by template name or category..."
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 pl-10 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
            />
          </label>

          {/* Category Filter Dropdown */}
          <div className="relative min-w-[180px]">
            <select
              value={state.selectedCategory}
              onChange={(e) => {
                actions.setSelectedCategory(e.target.value);
                actions.setPage(1);
              }}
              className="w-full appearance-none rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 pr-8 text-xs font-medium text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs cursor-pointer"
            >
              <option value="">All Categories</option>
              {state.categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-outline text-sm">
              expand_more
            </span>
          </div>

          {(state.searchText || state.selectedCategory) && (
            <button
              type="button"
              onClick={() => {
                actions.resetFilters();
                actions.setSelectedCategory('');
              }}
              className="inline-flex items-center gap-1 shrink-0 rounded-xl bg-surface-container px-3.5 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">filter_alt_off</span>
              <span>Clear</span>
            </button>
          )}
        </div>

        {/* Status Filter Tabs & Tools */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.setViewMode(tab.key)}
                className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  state.viewMode === tab.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <ToolsButton
            tools={toolsOptions}
            onSelect={actions.handleToolSelect}
            disabled={state.isExporting}
            triggerClassName={`h-9 px-3.5 bg-surface-container-highest hover:bg-outline-variant/40 text-on-surface-variant font-bold rounded-xl text-xs flex items-center gap-2 cursor-pointer transition select-none shadow-2xs ${
              state.isExporting ? 'opacity-60 pointer-events-none' : ''
            }`}
          />
        </div>
      </section>

      {state.error && (
        <div className="rounded-xl border border-error/30 bg-error-container/20 px-4 py-3 text-xs text-error font-medium">
          {state.error}
        </div>
      )}

      {/* Main Data Table */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-outline-variant/20 bg-surface-container-lowest text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="py-3.5 pl-6 pr-4">Template Name</th>
                <th className="px-4 py-3.5">Category</th>
                <th className="px-4 py-3.5">Content Preview</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5">Created Date</th>
                <th className="py-3.5 pl-4 pr-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10 text-on-surface">
              {state.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span className="text-xs font-medium">Loading document templates...</span>
                    </div>
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-4xl text-outline-variant">description</span>
                      <p className="text-sm font-bold text-on-surface">No document templates found</p>
                      <p className="text-xs">Try adjusting your search criteria or create a new template via "+ Create New".</p>
                      {(state.searchText || state.selectedCategory) && (
                        <button
                          type="button"
                          onClick={() => {
                            actions.resetFilters();
                            actions.setSelectedCategory('');
                          }}
                          className="mt-2 text-xs font-bold text-primary hover:underline cursor-pointer"
                        >
                          Reset filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/masters/employee-document-master/entry?id=${row.id}`)}
                    className="group cursor-pointer transition hover:bg-surface-container-low/50"
                  >
                    <td className="py-3.5 pl-6 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-on-primary transition">
                          <span className="material-symbols-outlined text-base">description</span>
                        </div>
                        <div>
                          <span className="font-bold text-on-surface group-hover:text-primary transition">
                            {row.name}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-surface-container-high px-2.5 py-1 text-[11px] font-semibold text-on-surface-variant">
                        <span className="material-symbols-outlined text-[13px]">folder</span>
                        <span>{row.category_name || 'General'}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-on-surface-variant max-w-[280px] truncate">
                      {row.content ? (
                        <span className="truncate block font-mono text-[11px]">
                          {row.content.substring(0, 75)}...
                        </span>
                      ) : (
                        <span className="text-outline italic">No content</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                          row.status === 'Active'
                            ? 'bg-success-container/60 text-success'
                            : 'bg-surface-container-high text-on-surface-variant'
                        }`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        <span>{row.status}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-on-surface-variant text-[11px]">
                      {row.created_on || '—'}
                    </td>
                    <td
                      className="py-3.5 pl-4 pr-6 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AppActionMenu
                        triggerIcon="more_vert"
                        triggerClassName="rounded-lg p-1.5 text-outline-variant hover:bg-surface-container-high hover:text-primary transition cursor-pointer"
                        items={[
                          {
                            key: 'edit',
                            label: 'Edit',
                            icon: 'edit',
                            onClick: () =>
                              navigate(`/masters/employee-document-master/entry?id=${row.id}`),
                          },
                          {
                            key: 'archive',
                            label: row.status === 'Active' ? 'Archive' : 'Restore',
                            icon: row.status === 'Active' ? 'archive' : 'unarchive',
                            onClick: () => actions.handleArchive(row.id),
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: 'delete',
                            danger: true,
                            onClick: () => actions.handleDelete(row.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {state.totalCount > 0 && (
          <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest">
            <p className="text-xs text-on-surface-variant font-medium">
              Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
              {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
            </p>
            <AppPagination
              page={state.page}
              totalPages={state.totalPages}
              onPageChange={actions.setPage}
              onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
              onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
              canPrevious={state.page > 1}
              canNext={state.page < state.totalPages}
            />
          </div>
        )}
      </section>
    </div>
  );
}
